import { Alert, Loader, Stack, Text, Title } from '@mantine/core';
import { useIntl } from 'react-intl';

import { FreightRateCard } from './FreightRateCard';
import { ratesPanelMessages } from './rates-panel-messages';
import styles from './RatesPanel.module.css';
import { FREIGHT_QUOTE_ERROR_CODE } from '../../../../ltl/constants';
import { FreightNoCarriersMessage } from '../../../freight-no-carriers-message/freight-no-carriers-message';
import { FilteredRatesEmptyState } from '../../../freight-rate-card/FilteredRatesEmptyState';
import { RateFilterMenu } from '../../../freight-rate-card/RateFilterMenu';
import { RateSearchInput } from '../../../freight-rate-card/RateSearchInput';
import { RateSortMenu } from '../../../freight-rate-card/RateSortMenu';
import { useRateRows } from '../../../freight-rate-card/use-rate-rows';

import type { LtlQuote } from '../../../../types/ltl';
import type { ReactNode } from 'react';

type RatesPanelProps = {
  rates: LtlQuote[];
  isLoading: boolean;
  isInsured: boolean;
  rateError?: ReactNode;
  rateErrorCode?: string | null;
  selectedOfferId?: string;
  onSelectQuote: (quote: LtlQuote) => void;
  sellerFreightProviderId?: number;
  zIndex?: number;
};

export const RatesPanel = ({
  rates,
  isLoading,
  isInsured,
  rateError,
  rateErrorCode,
  selectedOfferId,
  onSelectQuote,
  sellerFreightProviderId,
  zIndex,
}: RatesPanelProps) => {
  const intl = useIntl();
  const {
    carrierRows,
    sortOption,
    setSortOption,
    filters,
    setFilters,
    clearRateFilters,
    hasFilteredOutAllRates,
    searchTerm,
    isSearchOpen,
    openSearch,
    closeSearch,
    collapseEmptySearch,
    handleSearchChange,
  } = useRateRows({
    rates,
    isInsured,
    isLoading,
  });
  const isQuoteIneligible = rateErrorCode === FREIGHT_QUOTE_ERROR_CODE.QuoteIneligible;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Title order={4}>{intl.formatMessage(ratesPanelMessages.title)}</Title>
        <div className={styles.headerActions}>
          {carrierRows.length > 0 && (
            <Text c='dimmed' className={styles.carriersAvailable} size='sm'>
              {intl.formatMessage(ratesPanelMessages.carriersAvailable, { count: carrierRows.length })}
            </Text>
          )}
          <RateSortMenu
            onSortOptionChange={setSortOption}
            onTriggerInteract={collapseEmptySearch}
            sortOption={sortOption}
            zIndex={zIndex}
          />
          <RateFilterMenu filters={filters} onApplyFilters={setFilters} onTriggerInteract={collapseEmptySearch} zIndex={zIndex} />
          <RateSearchInput
            isOpen={isSearchOpen}
            onClose={closeSearch}
            onOpen={openSearch}
            onSearchChange={handleSearchChange}
            searchTerm={searchTerm}
          />
        </div>
      </div>
      <Stack className={styles.body} gap='md'>
        {rateError && !isQuoteIneligible && (
          <Alert color='red' data-testid='rate-error'>
            {rateError}
          </Alert>
        )}
        {isLoading && (
          <div className={styles.loaderWrapper}>
            <Loader data-testid='rates-loader' />
          </div>
        )}
        {!isLoading && isQuoteIneligible && carrierRows.length === 0 && (
          <div className={styles.emptyState}>
            <FreightNoCarriersMessage />
          </div>
        )}
        {hasFilteredOutAllRates && <FilteredRatesEmptyState onClearFilters={clearRateFilters} />}
        {!isLoading && !hasFilteredOutAllRates && rates.length === 0 && !rateError && !isQuoteIneligible && (
          <Text className={styles.emptyState} c='dimmed' data-testid='rates-empty'>
            {intl.formatMessage(ratesPanelMessages.noRates)}
          </Text>
        )}
        {!isLoading && carrierRows.length > 0 && (
          <div className={styles.ratesList}>
            {carrierRows.map((carrierRow) => (
              <FreightRateCard
                carrierRow={carrierRow}
                key={carrierRow.carrierKey}
                onSelectRate={onSelectQuote}
                selectedOfferId={selectedOfferId}
                sellerFreightProviderId={sellerFreightProviderId}
                zIndex={zIndex}
              />
            ))}
          </div>
        )}
      </Stack>
    </div>
  );
};
